/**
 * Typed client for the analytics endpoints (`/api/analytics/*`).
 *
 * Uses the newer `apiClient` from `./client` (not the legacy raw-fetch `api-client.ts` pattern used
 * by `courses.ts`/`dashboard.ts`). The backend DTOs are camelCase-only, so no snake_case adapter
 * layer is introduced here.
 */
import { apiClient } from "./client";
import { buildQueryString } from "./query-string";

// ---------------------------------------------------------------------------
// Shared filter shape — mirrors LMSAPI.BLL.Dtos.Analytics.AnalyticsFilterDto
// ---------------------------------------------------------------------------

/**
 * Every dimension is an ARRAY: the filter bar is multi-select, and a list of one covers the single
 * case. They serialise as repeated query params, which is what the backend's `List<T>` binds from.
 *
 * `trainingStatuses` narrows the STUDENT population on every tab; `enrollmentStatuses` narrows
 * per-course progression. They used to be one `status` string that each endpoint re-parsed as a
 * different enum, so a selection filtered three unrelated things and silently did nothing on the two
 * tabs whose enum did not contain the chosen name.
 */
export interface AnalyticsFilters {
  from?: string;
  to?: string;
  courseIds?: string[];
  categories?: string[];
  batchIds?: string[];
  groupIds?: string[];
  teamLeaderIds?: string[];
  trainingStatuses?: string[];
  enrollmentStatuses?: string[];
  studentId?: string;
}

// ---------------------------------------------------------------------------
// Filter facets — GET /analytics/facets
// ---------------------------------------------------------------------------

export interface CourseFacet {
  id: string;
  title: string;
  category: string | null;
}

/** A dropdown option; `parentId` is set where the dimension cascades (a group's batch). */
export interface FacetOption {
  id: string;
  name: string;
  parentId?: string | null;
}

export interface AnalyticsFacets {
  courses: CourseFacet[];
  categories: string[];
  batches: FacetOption[];
  groups: FacetOption[];
  teamLeaders: FacetOption[];
  trainingStatuses: string[];
  enrollmentStatuses: string[];
}

/** Every filter-bar option in one scoped call. */
export function fetchAnalyticsFacets(): Promise<AnalyticsFacets> {
  return apiClient.get<AnalyticsFacets>("/analytics/facets");
}

// ---------------------------------------------------------------------------
// Student analytics — GET /analytics/students
// ---------------------------------------------------------------------------

export interface GrowthTrendPoint {
  date: string;
  newStudents: number;
}

export interface EngagementTrendPoint {
  date: string;
  activeLearners: number;
  learningHours: number;
  modulesCompleted: number;
}

export interface TrainingStatusBreakdown {
  status: string;
  count: number;
  sharePercent: number;
}

export interface StudentAnalyticsDto {
  totalStudents: number;
  activeStudents: number;
  inactiveStudents: number;
  engagedStudents: number;
  activeRatePercent: number;
  averageCourseProgressPercent: number;
  growthTrend: GrowthTrendPoint[];
  engagementTrend: EngagementTrendPoint[];
  trainingStatusBreakdown: TrainingStatusBreakdown[];
}

export function fetchStudentAnalytics(filters?: AnalyticsFilters): Promise<StudentAnalyticsDto> {
  return apiClient.get<StudentAnalyticsDto>(`/analytics/students${buildQueryString(filters)}`);
}

// ---------------------------------------------------------------------------
// Course analytics — GET /analytics/courses
// ---------------------------------------------------------------------------

export interface EnrollmentTrendPoint {
  date: string;
  count: number;
}

export interface CompletionTrendPoint {
  date: string;
  cumulativeEnrollments: number;
  cumulativeCompletions: number;
  completionRatePercent: number;
}

export interface CoursePerformance {
  courseId: string;
  title: string;
  category: string | null;
  enrollmentCount: number;
  completedCount: number;
  inProgressCount: number;
  notStartedCount: number;
  withdrawnCount: number;
  completionRatePercent: number;
  dropOffRatePercent: number;
  averageProgressPercent: number;
}

export interface CourseAnalyticsDto {
  totalCourses: number;
  publishedCourses: number;
  draftCourses: number;
  totalEnrollments: number;
  completedEnrollments: number;
  inProgressEnrollments: number;
  notStartedEnrollments: number;
  withdrawnEnrollments: number;
  completionRatePercent: number;
  dropOffRatePercent: number;
  stalledEnrollments: number;
  stalledRatePercent: number;
  stallThresholdDays: number;
  averageProgressPercent: number;
  enrollmentTrend: EnrollmentTrendPoint[];
  completionTrend: CompletionTrendPoint[];
  mostPopularCourses: CoursePerformance[];
  leastPopularCourses: CoursePerformance[];
  highestCompletionRateCourses: CoursePerformance[];
  lowestCompletionRateCourses: CoursePerformance[];
  highestDropOffCourses: CoursePerformance[];
}

export function fetchCourseAnalytics(filters?: AnalyticsFilters): Promise<CourseAnalyticsDto> {
  return apiClient.get<CourseAnalyticsDto>(`/analytics/courses${buildQueryString(filters)}`);
}

// ---------------------------------------------------------------------------
// Module analytics — GET /analytics/modules
// ---------------------------------------------------------------------------

export interface DailyLearningTrendPoint {
  date: string;
  hours: number;
  activeLearners: number;
}

export interface ModulePerformance {
  moduleId: string;
  title: string;
  courseId: string;
  courseTitle: string;
  startCount: number;
  completedCount: number;
  completionRatePercent: number;
  dropOffRatePercent: number;
  averageWatchHours: number;
}

export interface ModuleAnalyticsDto {
  totalLearningHours: number;
  learningHoursInWindow: number;
  learningHoursToday: number;
  learningHoursThisWeek: number;
  learningHoursThisMonth: number;
  averageHoursPerActiveStudent: number;
  /** Null until the per-day learning table has recorded anything — see the Modules tab notice. */
  learningTelemetryStartsAt: string | null;
  dailyLearningTrend: DailyLearningTrendPoint[];
  modulesStarted: number;
  modulesCompleted: number;
  moduleCompletionRatePercent: number;
  averageModuleCompletionTimeHours: number;
  mostStartedModules: ModulePerformance[];
  leastStartedModules: ModulePerformance[];
  highestDropOffModules: ModulePerformance[];
}

export function fetchModuleAnalytics(filters?: AnalyticsFilters): Promise<ModuleAnalyticsDto> {
  return apiClient.get<ModuleAnalyticsDto>(`/analytics/modules${buildQueryString(filters)}`);
}

// ---------------------------------------------------------------------------
// Quiz performance analytics — GET /analytics/quiz-performance
// ---------------------------------------------------------------------------

export interface QuestionDifficulty {
  questionId: string;
  questionText: string;
  category?: string | null;
  authoredDifficulty?: string | null;
  timesAnswered: number;
  percentCorrect: number;
  difficultyIndex: number;
  difficultyBand: string;
}

export interface CategoryAccuracy {
  category: string;
  timesAnswered: number;
  accuracyPercent: number;
}

export interface ScoreDistributionBucket {
  rangeStart: number;
  rangeEnd: number;
  label: string;
  attemptCount: number;
  sharePercent: number;
}

export interface ExamPerformance {
  examId: string;
  title: string;
  gradedAttempts: number;
  averageScore: number;
  passRatePercent: number;
}

export interface QuizPerformanceAnalyticsDto {
  totalAttempts: number;
  gradedAttempts: number;
  passedAttempts: number;
  failedAttempts: number;
  abandonedAttempts: number;
  pendingGradingAttempts: number;
  inProgressAttempts: number;
  averageScore: number;
  passRatePercent: number;
  failRatePercent: number;
  abandonRatePercent: number;
  averagePassingTimeSeconds: number;
  averageAttemptTimeSeconds: number;
  scoreDistribution: ScoreDistributionBucket[];
  questionSampleThreshold: number;
  hardestQuestion?: QuestionDifficulty | null;
  easiestQuestion?: QuestionDifficulty | null;
  hardestQuestions: QuestionDifficulty[];
  examAccuracyByCategory: CategoryAccuracy[];
  examBreakdown: ExamPerformance[];
}

export function fetchQuizPerformanceAnalytics(
  filters?: AnalyticsFilters,
): Promise<QuizPerformanceAnalyticsDto> {
  return apiClient.get<QuizPerformanceAnalyticsDto>(
    `/analytics/quiz-performance${buildQueryString(filters)}`,
  );
}

// ---------------------------------------------------------------------------
// CV insights analytics — GET /analytics/cv-insights
// ---------------------------------------------------------------------------

export interface NamedCount {
  name: string;
  count: number;
}

export interface CvInsightsAnalyticsDto {
  totalStudents: number;
  totalStudentsWithCv: number;
  totalAiAnalyzed: number;
  pendingAiAnalysisCount: number;
  failedAiAnalysisCount: number;
  cvUploadRatePercent: number;
  aiAnalysisRatePercent: number;
  topSkills: NamedCount[];
  topEducationInstitutions: NamedCount[];
  topExperienceTitles: NamedCount[];
  topLanguages: NamedCount[];
}

export function fetchCvInsightsAnalytics(
  filters?: AnalyticsFilters,
): Promise<CvInsightsAnalyticsDto> {
  return apiClient.get<CvInsightsAnalyticsDto>(
    `/analytics/cv-insights${buildQueryString(filters)}`,
  );
}

// ---------------------------------------------------------------------------
// Certificate analytics — GET /analytics/certificates
// ---------------------------------------------------------------------------

export interface CertificateIssuanceTrendPoint {
  date: string;
  issuedCount: number;
}

export interface CertificateBreakdown {
  name: string;
  count: number;
  sharePercent: number;
}

export interface CertificateAnalyticsDto {
  totalCertificates: number;
  issuedCount: number;
  pendingCount: number;
  generatingCount: number;
  revokedCount: number;
  replacedCount: number;
  failedCount: number;
  issuanceCoveragePercent: number;
  revocationRatePercent: number;
  verifiableCount: number;
  awaitingPdfCount: number;
  averageDaysToIssue: number;
  issuanceTrend: CertificateIssuanceTrendPoint[];
  byType: CertificateBreakdown[];
  byStatus: CertificateBreakdown[];
}

export function fetchCertificateAnalytics(
  filters?: AnalyticsFilters,
): Promise<CertificateAnalyticsDto> {
  return apiClient.get<CertificateAnalyticsDto>(
    `/analytics/certificates${buildQueryString(filters)}`,
  );
}

// ---------------------------------------------------------------------------
// Team leader analytics — GET /analytics/team-leaders
// ---------------------------------------------------------------------------

export interface TeamLeaderPerformance {
  teamLeaderId: string;
  name: string;
  groupCount: number;
  studentCount: number;
  activeStudentCount: number;
  activeRatePercent: number;
  enrollmentCount: number;
  completedEnrollmentCount: number;
  completionRatePercent: number;
  averageProgressPercent: number;
  examAttemptCount: number;
  examPassRatePercent: number;
  certificateCount: number;
  learningHoursInWindow: number;
  atRiskStudentCount: number;
}

export interface TeamLeaderAnalyticsDto {
  leaders: TeamLeaderPerformance[];
  unassignedStudentCount: number;
}

export function fetchTeamLeaderAnalytics(
  filters?: AnalyticsFilters,
): Promise<TeamLeaderAnalyticsDto> {
  return apiClient.get<TeamLeaderAnalyticsDto>(
    `/analytics/team-leaders${buildQueryString(filters)}`,
  );
}
